import { useEffect, useRef, useState, useMemo } from 'react';
import maplibregl, { type Map as CarteGL, type MapGeoJSONFeature } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { attribution, SOURCES } from '../../src/partage/sources.js';
import { paletteCarte, type PaletteCarte } from './palette-carte.js';
import {
  expressionLargeur, expressionLargeurSelon, expressionGaineSelon, motifDe,
} from './signatures-plan';
import { BATI_EFFACE, couleurPreuve, type StylePlan } from './styles-plan';
import {
  AFFICHAGE_DEFAUT, COUCHES_COMMUTEES, modeAffichage, type Affichage, type ModeAffichage,
} from './affichages-carte';
import { useThemeActif } from './theme.js';
import { nomImageTrame } from './trames-etat';
import { poserImagesTrames } from './trames-peinture';

/**
 * LES COULEURS DE FOND SUIVENT LE THÈME.
 *
 * MapLibre n'accepte pas `var()` : un style ne reçoit que des littéraux. On lit
 * donc les jetons à la construction.
 *
 * ⚠️ LA CARTE SE DÉTRUIT ET SE REBÂTIT AU BASCULEMENT DE THÈME : `theme` est
 * dans les dépendances de l'effet constructeur, son nettoyage fait
 * `c.remove()`, et les jetons sont relus au rebâti (theme.tsx le documente).
 * Un commentaire antérieur affirmait ici l'inverse (« le thème n'est pris
 * qu'au montage ») — état révolu, retiré par la revue du 12/10/2026 : un
 * commentaire périmé fait conclure qu'une reconstruction est impossible.
 * L'affichage courant (PC§27) est réappliqué au rebâti via `refAffichage`.
 */
/**
 * PC§27 — applique un AFFICHAGE à la carte vivante : visibilité des couches
 * commutées, puis la caméra. Deux appelants, une fonction : la construction
 * (une carte rebâtie doit retrouver son affichage) et l'effet de bascule.
 *
 * ⚠️ On ne touche QUE les couches de `COUCHES_COMMUTEES` : écrire la
 * visibilité des autres écraserait les cases que l'agent a cochées dans le
 * panneau Couches. Et `jumpTo`, jamais `flyTo` — refus D.951.
 */
function appliquerAffichage(c: CarteGL, mode: ModeAffichage): void {
  for (const id of COUCHES_COMMUTEES) {
    if (!c.getLayer(id)) continue;
    c.setLayoutProperty(id, 'visibility',
      mode.couchesEteintes.includes(id) ? 'none' : 'visible');
  }
  if (c.getPitch() !== mode.pitch) c.jumpTo({ pitch: mode.pitch });
}

const jetonCarte = (n: string, repli: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(n).trim() || repli;

/**
 * CARTE PROFESSIONNELLE (§4) — native à Cadastral, jamais un renvoi vers Maps.
 *
 * ⚠️ AUCUNE TUILE EXTERNE. Le fond est uni et les formes viennent de nos
 * propres couches. Un fond de carte tiers ferait dépendre l'affichage d'un
 * registre foncier d'État de la disponibilité — et de la lecture des requêtes —
 * d'un fournisseur étranger. La règle « 0 tuile externe avec labels » de
 * l'écosystème est ici une règle de souveraineté, pas d'esthétique.
 */

/**
 * Empreinte du JEU DE GLYPHES servi. À changer dès que le contenu de
 * `frontend/public/polices/` change — c'est la seule chose qui force un
 * navigateur à redemander une ressource servie `immutable`.
 */
const VERSION_GLYPHES = 'noto-sans-20260912';

/** Conakry — le territoire servi par cette instance. */
const VUE_INITIALE = { centre: [-13.6773, 9.5370] as [number, number], zoom: 12 };

export interface SelectionCarte {
  couche: 'parcelles' | 'batiments' | 'divisions' | 'sections';
  identifiant: string;
  proprietes: Record<string, unknown>;
}

/**
 * CE QUE LA CARTE REGARDE — l'échelon courant de la cascade cadastrale.
 *
 * ⚠️⚠️ IL N'Y A PLUS QU'UNE CARTE, ET C'EST CE CONTRAT QUI LE PERMET. Jusqu'au
 * 22/09/2026 le plan cadastral vivait dans un SECOND écran (`assemblage.tsx`,
 * 1 290 lignes de `<canvas>`) qui redessinait les mêmes sections depuis une
 * autre route, avec son propre cadrage et son propre texte d'attribution — dont
 * un calculé ici et un écrit en dur là-bas. Deux représentations du même objet
 * divergent au premier correctif appliqué à une seule : c'est le défaut du SDK
 * de Connect, forké en deux exemplaires. Le tableau d'assemblage, les feuilles
 * et l'impression sont devenus des PANNEAUX de cette carte ; le canvas est
 * retiré.
 *
 * ⚠️⚠️ LA POSITION N'EST PAS UNE SÉLECTION — item D.997, signalé par le
 * fondateur le 24/09/2026. Ce contrat portait une seule `collectivite`, lue
 * SOUS LE CENTRE à chaque `moveend` : le panneau de droite changeait au simple
 * défilement, et ça se lisait comme une sélection qu'on n'a pas faite. Pire,
 * une section CHOISIE au clic était effacée dès que la collectivité sous le
 * centre changeait — une sélection détruite par un geste de navigation.
 * Le contrat §59 lie l'accès à la parcelle SÉLECTIONNÉE, jamais au centrage.
 *
 * Les deux canaux sont donc séparés et NOMMÉS :
 *   - `position`     : ce qui est sous le centre de l'écran — une donnée de
 *                      BARRE D'ÉTAT, elle bouge avec la carte ;
 *   - `collectivite` : la collectivité CHOISIE — par clic sur la carte, par
 *                      la liste du pays, ou en adoptant la position. Elle ne
 *                      bouge qu'au geste, comme `section`.
 */
type CollectiviteCarte = {
  sourceId: number; nom: string; code: string | null; echelon: string;
  sections: number; parcelles: number; gelees: number; peuplee: boolean;
};
export interface ContexteCarte {
  zoom: number;
  /** La collectivité CHOISIE — le sommet de la cascade, posé par un geste. */
  collectivite: CollectiviteCarte | null;
  /** La collectivité sous le centre de l'écran — information de position. */
  position: CollectiviteCarte | null;
  /**
   * La section CHOISIE au clic, jamais celle sous le curseur.
   *
   * ⚠️ LE COUPLE (code, collectivité) EST PORTÉ ENTIER. Mesuré le 21/09 :
   * 1 515 codes de section sur 1 552 sont portés par PLUSIEURS collectivités,
   * et `AAA` existe dans les 331. Un panneau qui ne recevrait que le code
   * demanderait une section au hasard parmi une centaine, sans que rien ne le
   * signale.
   */
  section: {
    id: string; code: string; collectivite: number;
    origine: 'voie' | 'grille'; surfaceM2: number;
  } | null;
}

interface Props {
  onSelection: (s: SelectionCarte | null) => void;
  /**
   * LEQUEL DES DEUX PLANS — « état juridique » ou « qualité du levé ».
   *
   * ⚠️ DEUX STYLES, PAS UNE OPTION D'AFFICHAGE. Le plan répondait à deux
   * questions dans un seul jeu de couleurs : l'état d'une parcelle et la
   * fiabilité de sa géométrie. D'où la pénurie de fentes de teinte, et d'où le
   * fait que « en instruction » se soit retrouvé à 40° du bâti. Le Kadaster
   * néerlandais publie `standaardvisualisatie` et `kwaliteitsvisualisatie`
   * pour exactement cette raison.
   */
  stylePlan?: StylePlan;
  onStylePlan?: (s: StylePlan) => void;
  /**
   * L'AFFICHAGE — PC§27 : cadastral (défaut), 3D, satellite, orthophoto.
   * Axe ORTHOGONAL au plan (`stylePlan`) : l'affichage choisit ce qui est
   * MONTRÉ (couches, caméra), le plan comment les limites sont PEINTES.
   */
  affichage?: Affichage;
  /**
   * L'INSTANCE MAPLIBRE, REMISE AU PARENT — et rendue à `null` au démontage.
   *
   * ⚠️ CETTE PROP EXISTE POUR QU'IL N'Y AIT JAMAIS UNE SECONDE CARTE. Ce dépôt
   * a déjà payé ce défaut : `carte.tsx` et `assemblage.tsx` dessinaient tous
   * deux les sections, chacun avec son cadrage, son zoom et son attribution —
   * dont l'une écrite en dur et déjà fausse. L'outil de tracé a besoin de
   * projeter écran ↔ terrain et de peindre sa figure ; il le fait SUR cette
   * carte-ci, jamais sur une carte à lui.
   */
  onCarte?: (c: CarteGL | null) => void;
  /**
   * Le gabarit d'URL des tuiles, SERVI par `GET /tuiles/version`
   * (ex. `/tuiles/v12/{z}/{x}/{y}.pbf`).
   *
   * ⚠️⚠️ SANS LUI, LA CARTE NE SUIT PAS L'ÉDITION. Le chemin non versionné est
   * servi en `max-age=300` : après un dépôt, le navigateur resservait sa tuile
   * cinq minutes — 200, tuile valide, simplement vieille — et l'agent
   * concluait que son enregistrement n'avait rien fait. La version change à
   * chaque écriture (migration 231), donc l'URL change, donc la tuile est
   * redemandée.
   *
   * ⚠️ FACULTATIF, ET LE REPLI EST LE CHEMIN NON VERSIONNÉ. Si
   * `/tuiles/version` ne répond pas, une carte à 5 minutes de retard vaut
   * infiniment mieux qu'une carte noire.
   */
  gabaritTuiles?: string;
  /**
   * Suspend la sélection au clic — l'outil de tracé s'en sert pendant qu'il
   * pose des sommets. Voir le garde dans le gestionnaire `click`.
   */
  suspendu?: boolean;
  /** Ce que la carte regarde — alimente les panneaux de la cascade. */
  onContexte?: (c: ContexteCarte) => void;
  /**
   * Où aller, sur demande d'un panneau. `cle` change à chaque demande : sans
   * elle, redemander la MÊME destination (« recentrer sur cette commune »)
   * ne déclencherait rien, l'objet étant inchangé.
   */
  cible?: { centre: [number, number]; zoom: number; cle: string } | null;
  /**
   * Une section choisie AILLEURS QUE SUR LA CARTE — au tableau d'assemblage,
   * par exemple. `cle` change à chaque demande, et `section: null` referme.
   *
   * ⚠️ POURQUOI UNE CLÉ PLUTÔT QUE LA VALEUR. Refermer la section puis
   * rouvrir LA MÊME ne changerait pas l'objet : sans clé, le second geste ne
   * déclencherait rien, et l'agent croirait le tableau cassé.
   */
  sectionImposee?: { cle: string; section: ContexteCarte['section'] } | null;
  /**
   * La collectivité CHOISIE depuis un panneau (liste du pays, adoption de la
   * position) — adoptée sur sa clé, comme `sectionImposee` et pour la même
   * raison : une valeur identique re-imposée doit quand même s'appliquer.
   */
  collectiviteImposee?: { cle: string; collectivite: CollectiviteCarte | null } | null;
  /**
   * La voie sous le curseur, en toutes lettres — pour la barre d'état. La
   * bulle au curseur reste, mais elle n'est plus EXCLUSIVE (refus D.951) :
   * la même information est lisible dans une région stable de l'écran.
   */
  onVoieSurvolee?: (texte: string | null) => void;
  /** Les parcelles retenues dans la TABLE — reflétées au trait (D.948). */
  selectionTable?: readonly string[];
  parcelleActive?: string | null;
  /**
   * ⚠️ `sections` EST DANS LE CONTRAT PARCE QUE LE SERVICE LE SERT. Il
   * manquait ici alors que `/tuiles/reglages` le renvoie depuis le 22/09 : le
   * panneau qui en a besoin le recopiait donc en dur, et deux tables du même
   * seuil divergent au premier réglage.
   */
  seuils?: { divisions: number; sections?: number; parcelles: number; batiments: number };
  /** Zoom d'apparition des noms de rue, SERVI par le service (jamais recopié). */
  zoomNomsDeRue?: number;
  /** Sous ce zoom, seul le bâti RATTACHÉ au registre est servi (7,4 % du total). */
  zoomBatiComplet?: number;
  /** Où le registre a de la matière. `null` = il n'en a nulle part. */
  vueConseillee?: {
    centre: [number, number]; zoom: number;
    division: string | null; parcelles: number; motif: string;
  } | null;
  couverture?: {
    /**
     * ⚠️ DEUX COMPTES, ET LES CONFONDRE ETAIT LE DEFAUT (D.779).
     * `parcellesAuPlan` est un `valide_au IS NULL` : il inclut 1 761 150
     * mailles en statut 'projet'. `parcellesOpposables` est le
     * `statut = 'active'` — 4 parcelles au 11/10/2026. Un facteur 440 000.
     */
    parcellesAuPlan: number; parcellesOpposables: number;
    /** @deprecated D.779 — portait le sens « au plan » sous le nom « actives ». */
    parcellesActives: number; divisionsPeuplees: number;
    /** La répartition des `cad.origine_donnee`, servie par /tuiles/reglages. */
    origines?: Record<string, number>;
    /**
     * ⚠️ LA POPULATION D'UNE DISTINCTION, AVANT DE LA MONTRER. Mesuré le
     * 22/09/2026 : 0 emprise sur 1 744 535 est non rattachée, donc les couches
     * filtrées sur ce cas ne dessinent RIEN — et la légende affirmait quand
     * même « bâti — non rattaché ». Une distinction vide et une distinction
     * non affichée se ressemblent trait pour trait : l'agent conclut qu'il n'y
     * a pas d'orpheline dans son quartier alors que la carte n'a pas regardé.
     */
    batiments?: number; batimentsNonRattaches?: number;
  };
}

/**
 * LES QUATRE ÉTATS D'UNE PARCELLE — les seules TEINTES du plan.
 *
 * ⚠️ « ACTIVE » ÉTAIT EN OR, c'est-à-dire dans la couleur de l'ACCENT de marque,
 * celle-là même qui servait aussi d'avertissement. Une parcelle en règle n'est
 * pas un avertissement, et un accent n'est pas un état. Les quatre prennent
 * désormais les teintes du socle, et le reste du plan n'en porte aucune.
 *
 * ⚠️ LA MAILLE RESTE VISIBLE. Elle valait #3a4550 sur un fond #0e1418 : le tracé
 * existait et ne se voyait pas. « Discret » ne veut pas dire invisible — une
 * maille dont on ne distingue pas le bord ne délimite rien, et c'est 988 679
 * parcelles sur 988 695.
 */
const couleurStatut = (p: PaletteCarte): Array<string | string[]> => [
  'match', ['get', 'statut'],
  /* ⚠️⚠️ `projet` MANQUAIT, ET IL PORTE TOUT LE PARCELLAIRE.
   * Mesuré le 22/09/2026 : 1 761 153 parcelles vivantes sur 1 761 157 sont en
   * `projet` — contre 4 en `active`, 0 en `en_instruction`, 0 en `suspendue`.
   * La valeur n'était pas dans ce `match` : 99,9998 % du plan était donc peint
   * par le REPLI, c'est-à-dire par accident et non par règle. Le rendu était
   * juste par chance, et la légende l'appelait « maille (présomptive) » —
   * un mot qui désigne une ORIGINE (`maille_systematique`), pas un STATUT.
   * La migration 002 dit ce que `projet` est : « saisie en cours, non opposable ».
   * Garde : tests/themes-et-enums.test.ts. */
  'projet', p.maille,
  'active', p.statut.active,
  'en_instruction', p.statut.enInstruction,
  'suspendue', p.statut.suspendue,
  'retiree', p.statut.retiree,
  /* Le repli reste la maille : une valeur inconnue ne doit pas repeindre le
   * pays en rouge sur un aléa de donnée. C'est la GARDE qui attrape la dérive
   * d'enum, au moment de l'essai — pas l'écran, en production. */
  p.maille,
];

/**
 * L'ATTRIBUTION DES SOURCES — ce que la carte doit avouer, et qu'elle taisait.
 *
 * ⚠️ ELLE SE CALCULE DEPUIS LES COUCHES RÉELLEMENT PRÉSENTES. Une ligne écrite
 * en dur crédite la bonne base jusqu'au jour où une couche change de source —
 * et ce jour-là elle a toujours l'air juste. Ici, `attribution()` lit le
 * registre partagé `src/partage/sources.ts`, celui-là même que le SERVICE garde.
 *
 * ⚠️ REPLIÉE PAR DÉFAUT, MAIS JAMAIS ABSENTE. Les licences exigent que le
 * public soit informé de la source ; elles n'exigent pas d'écraser un plan
 * cadastral sous trois lignes de droit anglais. Le crédit court est toujours
 * visible ; le texte imposé, dont la clause de non-responsabilité, s'ouvre d'un
 * clic — et se retrouve en entier dans SOURCES_ET_LICENCES.md.
 */
function AttributionSources({ couches }: { couches: string[] }) {
  const [ouvert, setOuvert] = useState(false);
  const lignes = attribution(couches);
  if (lignes.length === 0) return null;
  const detaillees = SOURCES.filter((s) => s.couches.some((c) => couches.includes(c)));
  return (
    <div className="carte-attribution">
      <button
        type="button"
        className="carte-attribution-bouton"
        aria-expanded={ouvert}
        onClick={() => setOuvert((v) => !v)}
        title="Sources et licences des données affichées"
      >
        Sources : {detaillees.map((s) => s.libelle).join(' · ')}
      </button>
      {ouvert && (
        <div className="carte-attribution-detail" role="note">
          {detaillees.map((s) => (
            <p key={s.cle}>
              {/* ⚠️ TEXTE LITTÉRAL. La licence impose la formule, pas son idée. */}
              <strong>{s.licence}</strong> — {s.mentionAdaptee ?? s.mention}
              {s.responsabilite && <> {s.responsabilite}</>}
            </p>
          ))}
          <p className="carte-attribution-note">
            Mowibo Cadastral n'est ni produit ni avalisé par les organismes cités.
          </p>
        </div>
      )}
    </div>
  );
}

export function Carte({ onSelection, onContexte, cible, sectionImposee, collectiviteImposee, onVoieSurvolee, selectionTable, parcelleActive, seuils, zoomNomsDeRue, vueConseillee, couverture, stylePlan = 'etat', affichage = AFFICHAGE_DEFAUT, onCarte, suspendu = false, gabaritTuiles }: Props) {
  const conteneur = useRef<HTMLDivElement>(null);
  const carte = useRef<CarteGL | null>(null);
  /**
   * ⚠️ L'AGENT A-T-IL DÉJÀ BOUGÉ LA CARTE ? Un `ref`, jamais un état : le
   * recentrage différé doit lire la valeur au moment où il s'exécute, pas celle
   * capturée au rendu qui l'a programmé.
   */
  const aDejaBouge = useRef(false);
  const [zoom, setZoom] = useState(VUE_INITIALE.zoom);
  const [erreurTuiles, setErreurTuiles] = useState<string | null>(null);
  /* ⚠️ Les jetons d'état (zoom, centre, inclinaison, seuils) ont quitté ce
     fichier avec `.carte-etat` : ils vivent dans la BARRE D'ÉTAT (D.944),
     qui lit la carte elle-même. Ici ne reste que ce que la toile peint. */

  /**
   * L'ÉCHELON COURANT DE LA CASCADE — ce qui alimente les panneaux.
   *
   * ⚠️ `onContexte` EST TENU DANS UN `ref`, PAS DANS LES DÉPENDANCES. L'effet
   * qui bâtit la carte fait six cents lignes et porte `if (carte.current)
   * return` : le faire dépendre d'un rappel non mémoïsé par le parent le
   * relancerait à chaque rendu, donc jamais — et la carte resterait celle du
   * premier montage, silencieusement.
   */
  const refContexte = useRef(onContexte);
  refContexte.current = onContexte;
  const refVoieSurvolee = useRef(onVoieSurvolee);
  refVoieSurvolee.current = onVoieSurvolee;
  // Même raison que `refContexte` : l'effet de construction ne se rejoue pas.
  const refSuspendu = useRef(suspendu);
  refSuspendu.current = suspendu;
  const refOnCarte = useRef(onCarte);
  refOnCarte.current = onCarte;
  const refGabarit = useRef(gabaritTuiles);
  refGabarit.current = gabaritTuiles;
  /** La collectivité CHOISIE — par un clic, jamais par le défilement (D.997). */
  const [collectiviteChoisie, setCollectiviteChoisie] = useState<CollectiviteCarte | null>(null);
  /** La collectivité sous le centre — donnée de position, pour la barre d'état. */
  const [collectivitePosition, setCollectivitePosition] = useState<CollectiviteCarte | null>(null);
  const [sectionChoisie, setSectionChoisie] = useState<ContexteCarte['section']>(null);

  const theme = useThemeActif();
  const P = useMemo(() => paletteCarte(), [theme]);

  /**
   * LES DEUX PLANS — ce qui change entre « état » et « qualité », et rien d'autre.
   *
   * ⚠️ `useThemeActif()` rend 'light' | 'dark' ; les tables de style parlent
   * 'clair' | 'sombre'. Un seul point de traduction, ici : deux vocabulaires
   * pour la même chose se recopient de bonne foi jusqu'à ce que l'un des deux
   * gagne une valeur que l'autre ignore.
   */
  const fondCarte: 'clair' | 'sombre' = theme === 'light' ? 'clair' : 'sombre';
  const enQualite = stylePlan === 'qualite';
  /**
   * ⚠️ LA MÊME EXPRESSION SERT LE REMPLISSAGE ET LE CONTOUR DES PARCELLES.
   * Elles portaient deux appels séparés à `couleurStatut(P)` : deux expressions
   * qui n'avaient aucun moyen de rester d'accord, et dont l'écart se serait vu
   * comme un liseré d'une couleur et un corps d'une autre.
   */
  const couleurLimite = useMemo(
    () => (enQualite ? couleurPreuve(fondCarte) : couleurStatut(P)),
    [enQualite, fondCarte, P],
  );
  /**
   * ⚠️ MÊME RÈGLE POUR LE BÂTI : une seule expression, deux lecteurs — le
   * littéral du style à la construction, et l'application À CHAUD plus bas.
   * La revue du 12/10 a mesuré que le basculeur état/qualité ne repeignait la
   * toile qu'à la reconstruction suivante (changement de thème) : la légende
   * basculait, la carte non.
   */
  const couleurBati = useMemo(
    () => (enQualite
      ? BATI_EFFACE[fondCarte].corps
      : (['case', ['get', 'rattache'], P.bati, P.batiNon] as any)),
    [enQualite, fondCarte, P],
  );
  /* L'affichage courant, lisible par l'effet CONSTRUCTEUR sans en dépendre —
     même raison que `refContexte` : cet effet ne se rejoue que sur sa clé. */
  const refAffichage = useRef(affichage);
  refAffichage.current = affichage;

  /* ⚠️ Le basculeur « état / qualité » et son verdict (`offreQualite`) ont
     suivi la légende dans `legende-carte.tsx` : une légende qui décrit un plan
     et le bouton qui choisit ce plan vivent ensemble, ou divergent. */

  useEffect(() => {
    if (!conteneur.current || carte.current) return;

    const c = new maplibregl.Map({
      container: conteneur.current,
      style: {
        version: 8,
        /**
         * ⚠️ LES GLYPHES VIENNENT DE MOWIBO MAPS, ET CE N'EST PAS UNE DÉPENDANCE
         * EXTERNE. Ce style portait « aucune police distante », et j'en avais
         * conclu qu'une étiquette de rue exigerait un fournisseur tiers — donc
         * qu'elle était hors de portée. C'était une croyance, pas une mesure.
         *
         * Mowibo Maps sert SES PROPRES glyphes : `font-server.js`, « génère des
         * glyphs PBF compatibles MapLibre GL — 100 % propriétaire ». Mesuré le
         * 10-09 : `/tiles/fonts/Noto%20Sans%20Regular/0-255.pbf` rend
         * **114 770 octets** d'`application/x-protobuf`.
         *
         * ⚠️ TÉMOIN NON DISCRIMINANT, ET IL FAUT LE DIRE : une police inventée
         * rend AUSSI 200 — le serveur génère à la demande. On ne peut donc pas
         * prouver par le code HTTP qu'une police précise existe ; ce qui est
         * prouvé, c'est qu'on reçoit du vrai PBF.
         *
         * ⚠️ ET L'URL RESTE `location.origin` : le service RELAIE les glyphes
         * (`/tuiles/fonts/...`). Pointer directement sur Maps aurait rompu la
         * propriété tenue depuis la première session — le paquet ne connaît
         * qu'une seule origine — et rendu la carte d'un registre foncier
         * dépendante d'un fond de carte à chaque affichage.
         */
        /**
         * ⚠️⚠️ LA VERSION DANS L'URL N'EST PAS DÉCORATIVE — ELLE EST LA CORRECTION
         * D'UN DÉFAUT DE DÉPLOIEMENT QUI A RENDU LE CORRECTIF INVISIBLE.
         *
         * Les glyphes sont servis en `Cache-Control: max-age=31536000, immutable`.
         * Le 12/09/2026 j'ai remplacé leur CONTENU — les rectangles de Mowibo Maps
         * par de vrais SDF Noto Sans — SANS toucher à l'URL. Tout navigateur qui
         * avait déjà ouvert la carte gardait donc les glyphes cassés pour un an et
         * ne les redemandait jamais. Le service était corrigé, le déploiement
         * vérifié, et le fondateur voyait exactement la même carte noire.
         *
         * ⚠️ ET C'EST PIRE QU'UN TEXTE MANQUANT : un glyphe refusé fait échouer
         * l'analyse de la TUILE ENTIÈRE. L'écran reste noir, pas « sans noms ».
         *
         * ⚠️ LA VÉRIFICATION EN NAVIGATEUR NEUF NE POUVAIT PAS LE VOIR. Playwright
         * ouvre un profil vierge : aucun cache, donc toujours les bons glyphes.
         * Seul un navigateur ayant vu l'ancienne version manifeste le défaut —
         * c'est-à-dire précisément celui de l'utilisateur.
         *
         * ⚠️ CETTE CHAÎNE CHANGE À CHAQUE FOIS QUE LE CONTENU DES GLYPHES CHANGE.
         * Regénérer `frontend/public/polices/` sans la toucher reproduit le défaut
         * à l'identique. `tests/glyphes.test.ts` le garde.
         */
        glyphs: `${window.location.origin}/tuiles/fonts/{fontstack}/{range}.pbf?v=${VERSION_GLYPHES}`,
        /**
         * ⚠️ LE RELIEF EST SERVI PAR NOUS, DEPUIS NOS PROPRES TUILES COPERNICUS.
         * `terrarium` et les services de relief publics sont hébergés ailleurs :
         * les employer ferait dépendre le rendu d'un plan d'État de la
         * disponibilité — et de la lecture des requêtes — d'un tiers. Même règle
         * que pour le fond : 0 tuile externe.
         *
         * ⚠️ ET HORS COUVERTURE, LA TUILE EST TRANSPARENTE, PAS À ZÉRO : une
         * altitude absente encodée comme 0 m poserait une plaine au niveau de
         * la mer là où l'on ne sait rien.
         */
        sources: {
          releve: {
            type: 'raster-dem',
            tiles: [`${window.location.origin}/relief/{z}/{x}/{y}.png`],
            tileSize: 256,
            // Convention Mapbox Terrain-RGB : alt = -10000 + (R*65536+G*256+B)*0.1
            encoding: 'mapbox',
            // ⚠️ MÊME SEUIL QUE LE SERVICE (ZOOM_MINIMAL). En dessous, les
            // sources traversées ne se déduisent plus des quatre coins de la
            // tuile et le relevé serait vide sur un pays couvert. MapLibre
            // rééchantillonne lui-même vers les zooms inférieurs.
            minzoom: 10,
            maxzoom: 14,
          },
          cadastral: {
            type: 'vector',
            tiles: [`${window.location.origin}${refGabarit.current ?? '/tuiles/{z}/{x}/{y}.pbf'}`],
            minzoom: 0,
            maxzoom: 18,
          },
        },
        layers: [
          { id: 'fond', type: 'background', paint: { 'background-color': jetonCarte('--carte-fond', '#000000') } },
          {
            id: 'divisions-remplissage', type: 'fill', source: 'cadastral', 'source-layer': 'divisions',
            paint: { 'fill-color': jetonCarte('--carte-eau', '#0a0a0a'), 'fill-opacity': 0.5 },
          },
          /**
           * ⚠️⚠️ LA COLLECTIVITÉ — LE SOMMET DE LA CASCADE, ET IL MANQUAIT.
           * La couche `divisions` sert `ter.division`, un registre OSM dont
           * l'échelon 3 ne compte que 24 lignes ; les 153 248 sections pendent
           * de `ter.circonscription`, où 331 collectivités portent du cadastre.
           * `cad.section.division_id` est NULL sur la TOTALITÉ des lignes.
           * Cliquer une « commune » désignait donc un polygone sans aucun lien
           * avec les sections dessinées par-dessus — et les deux couches
           * rendaient chacune un objet valide, ce qui rendait le défaut muet.
           * Migration 210.
           *
           * ⚠️ LE REMPLISSAGE DIT OÙ LE CADASTRE EXISTE, ET C'EST TOUT SON
           * OBJET. 81 % des sections du pays ne portent aucune parcelle :
           * teinter les 331 collectivités du même ton donnerait à lire un
           * registre peuplé partout. `peuplee` voyage dans la tuile.
           */
          {
            id: 'collectivites-remplissage', type: 'fill', source: 'cadastral',
            'source-layer': 'collectivites',
            paint: {
              'fill-color': [
                'case', ['==', ['get', 'peuplee'], true],
                jetonCarte('--carte-collectivite', '#86dcec'),
                jetonCarte('--carte-eau', '#0a0a0a'),
              ],
              // Très basse : c'est un indice de densité, pas un aplat. Au-delà
              // de z12 il s'efface — la section a pris le relais et deux
              // remplissages superposés rendent le trait illisible.
              'fill-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.10, 10, 0.08, 12, 0.03, 13, 0],
            },
          },
          /**
           * ⚠️ LE TRAIT LE PLUS LOURD DU PLAN, et c'est une règle de lecture.
           * La collectivité est la limite DANS LAQUELLE un code de section
           * devient unique : 1 515 codes sur 1 552 sont portés par plusieurs
           * collectivités. Dessinée du même trait qu'une limite d'orientation,
           * elle ferait lire « AAA » comme un identifiant.
           */
          {
            id: 'collectivites-contour', type: 'line', source: 'cadastral',
            'source-layer': 'collectivites',
            paint: {
              'line-color': jetonCarte('--carte-collectivite', '#86dcec'),
              'line-width': expressionLargeur('collectivite') as any,
              'line-dasharray': motifDe('collectivite') as any,
              'line-opacity': 0.9,
            },
          },
          /**
           * ⚠️ LA COLLECTIVITÉ COURANTE EST SOULIGNÉE, sinon le panneau parle
           * d'une commune que rien ne désigne à l'écran. Le filtre est posé
           * sur `source_id`, jamais sur le nom : « Lambanyi » existe dans deux
           * préfectures.
           */
          {
            id: 'collectivite-active', type: 'line', source: 'cadastral',
            'source-layer': 'collectivites',
            filter: ['==', ['get', 'source_id'], -1],
            paint: {
              'line-color': jetonCarte('--carte-choix', '#ffffff'),
              'line-width': expressionLargeur('choix_collectivite') as any,
              'line-opacity': 0.85,
            },
          },
          {
            id: 'divisions-contour', type: 'line', source: 'cadastral', 'source-layer': 'divisions',
            paint: {
              /**
               * ⚠️ CE TRAIT VALAIT `#2c3e4d` SUR UN FOND `#0e1418` — un écart de
               * luminance de 0,03, sous le seuil où l'œil distingue une ligne
               * fine d'un fond uni. La limite de préfecture était DESSINÉE et
               * invisible, ce qui se lit exactement comme une couche absente.
               * Les niveaux hauts passent en bleu franc, les bas restent
               * discrets : ce sont deux lectures différentes de la carte.
               */
              'line-color': ['case',
                ['<=', ['get', 'niveau'], 1], P.division[0],   // région
                ['==', ['get', 'niveau'], 2], P.division[1],   // préfecture
                P.division[2]],                                // commune, quartier
              'line-opacity': ['case', ['<=', ['get', 'niveau'], 2], 0.95, 0.55],
              // Les niveaux hauts (région) portent un trait plus épais que les
              // quartiers : sans cela, 462 divisions forment un treillis illisible.
              'line-width': expressionLargeurSelon('niveau',
                [[1, 'division_1']], 'division_4') as any,
              // Le tiret distingue une limite ADMINISTRATIVE d'une limite de
              // propriété — deux natures juridiques que rien d'autre ne sépare
              // à l'écran, et que personne ne doit confondre sur un cadastre.
              'line-dasharray': motifDe('division_1') as any,
            },
          },
          /**
           * §4 — LES RUES. Elles étaient absentes : trois couches servies, pas
           * une voie. Un plan cadastral sans rue ne permet ni de confirmer
           * qu'une parcelle est bien celle qu'on croit, ni de dire à quelqu'un
           * où elle se trouve.
           *
           * ⚠️ DESSINÉES SOUS LE BÂTI ET SOUS LES PARCELLES. Ce sont un repère,
           * pas un objet du registre : posées au-dessus, elles barreraient les
           * limites de propriété — précisément ce que l'agent vient lire.
           *
           * ⚠️ DEUX TRAITS PAR RUE, ET C'EST CE QUI LES REND LISIBLES SUR FOND
           * SOMBRE : un halo large et sombre dessous, le trait clair dessus. Un
           * trait unique se confond avec les contours de division.
           */
          {
            id: 'rues-halo', type: 'line', source: 'cadastral', 'source-layer': 'rues',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': jetonCarte('--carte-halo', '#000000'),
              'line-width': expressionGaineSelon('rang',
                [[1, 'voirie_structurante'], [2, 'voirie_desserte']], 'voirie_locale') as any,
            },
          },
          {
            id: 'rues', type: 'line', source: 'cadastral', 'source-layer': 'rues',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              // Le rang se lit à la couleur ET à l'épaisseur : sur un écran
              // d'agent, la hiérarchie de voirie est ce qui oriente en premier.
              'line-color': ['case',
                ['==', ['get', 'rang'], 1], P.voie[0],
                ['==', ['get', 'rang'], 2], P.voie[1],
                P.voie[2]],
              'line-width': expressionLargeurSelon('rang',
                [[1, 'voirie_structurante'], [2, 'voirie_desserte']], 'voirie_locale') as any,
            },
          },
           /**
           * ⚠️⚠️ DEUX COUCHES, ET C'EST TOUT L'ENJEU DE LA 3D DU BÂTI RÉEL.
           * `fill-extrusion-height` rend un volume de hauteur NULLE quand la
           * propriété manque : un bâtiment jamais relevé s'afficherait comme un
           * bâtiment de plain-pied, et la carte donnerait à voir une ville basse
           * au lieu d'une ville non relevée. Mesuré le 18/09/2026 : ZÉRO des
           * 1 744 535 emprises connaît sa hauteur.
           *
           * On extrude donc UNIQUEMENT ce qui a une hauteur, et l'on peint le
           * reste à plat, dans une teinte qui dit « non relevé ». La maquette
           * devient vraie à mesure que le recensement remplit `niveaux` —
           * jamais d'un coup, et jamais par une moyenne.
           */
         {
            id: 'batiments', type: 'fill', source: 'cadastral', 'source-layer': 'batiments',
            /**
             * ⚠️⚠️ AUCUNE BORNE DE ZOOM — L'EMPRISE NE DISPARAÎT JAMAIS, ET
             * C'EST LA CORRECTION D'UN DÉFAUT SIGNALÉ PAR LE FONDATEUR :
             * « quand on zoome on ne voit plus l'extrusion ».
             *
             * Cette couche portait `maxzoom: 15` et cédait la place aux volumes.
             * Or la carte n'a AUCUN `pitch` : elle est vue du dessus, et une
             * `fill-extrusion` regardée à la verticale ne montre que son toit —
             * il n'y a pas de volume à voir. Passé z15, une emprise claire avec
             * son contour (`#7b8d99`) était donc remplacée par une forme plus
             * sombre et sans contour : le bâti semblait s'effacer alors qu'il
             * était toujours dessiné.
             *
             * L'emprise reste donc peinte à tous les zooms, et les volumes
             * s'AJOUTENT par-dessus quand on incline la vue. Il n'existe plus
             * aucun zoom auquel le bâti disparaît.
             */
            paint: {
              // Un bâti RATTACHÉ à une parcelle se distingue d'un bâti orphelin :
              // c'est l'état d'avancement du cadastre, lisible d'un coup d'œil.
              /* ⚠️ DANS LA VUE QUALITÉ, LE BÂTI SE RETIRE — leçon directe du
               * Kadaster, dont le style `kwaliteitsvisualisatie` passe les
               * emprises en #E6E6E6/#CDCDCD. Sans ce retrait, la masse du bâti
               * (1 744 535 emprises) écrase une rampe qui ne porte que sur des
               * limites, et la vue prétend montrer ce qu'on ne voit plus. */
              'fill-color': couleurBati,
              'fill-outline-color': P.batiTrait,
            },
          },
          {
            /**
             * ⚠️ `fill-outline-color` NE FAIT QU'UN LISERÉ D'UN PIXEL, ET IL
             * NE S'ÉPAISSIT PAS. Sur un quartier où les emprises se touchent,
             * un pixel gris ne sépare rien : deux bâtiments mitoyens se lisent
             * comme une seule masse. Une vraie couche `line` donne au bord une
             * largeur qui suit le zoom — c'est ce qui rend l'emprise NETTE.
             */
            id: 'batiments-contour', type: 'line', source: 'cadastral', 'source-layer': 'batiments',
            minzoom: 14,
            /**
             * ⚠️ DEUX COUCHES ET UN `filter`, JAMAIS UNE EXPRESSION. `line-dasharray`
             * est `cross-faded` dans la spec MapLibre : un `['match', ['get', …]]`
             * y fait refuser le style ENTIER — défaut déjà déployé une fois et vu
             * par le fondateur avant les tests. `tests/style-carte.test.ts` le tient.
             *
             * CELLE-CI EST LE BÂTI RATTACHÉ : trait PLEIN, parce qu'on sait à quelle
             * parcelle il appartient.
             */
            filter: ['==', ['get', 'rattache'], true] as any,
            paint: {
              'line-color': P.batiTrait,
              'line-width': expressionLargeur('emprise_rattachee') as any,
              'line-opacity': 0.95,
            },
          },
          {
            /**
             * LE BÂTI NON RATTACHÉ : même famille, trait TIRETÉ.
             *
             * ⚠️ LE TIRET N'EST PAS UN ORNEMENT — c'est la même règle que la section
             * tracée au carroyage et que la maille : plein = mesuré, tireté = présumé.
             * Une seule question posée partout de la même façon, au lieu de quatre
             * nuances de gris que personne ne distingue sur un parcellaire dense.
             *
             * ⚠️ ET LE CONTOUR PLUTÔT QU'UNE HACHURE : à z15 une emprise fait quelques
             * pixels, et une hachure y devient du bruit sur 1,7 M d'objets. Le trait,
             * lui, reste lisible à toute échelle.
             */
            id: 'batiments-contour-presume', type: 'line', source: 'cadastral',
            'source-layer': 'batiments', minzoom: 14,
            filter: ['!=', ['get', 'rattache'], true] as any,
            paint: {
              'line-color': P.batiNon,
              'line-width': expressionLargeur('emprise_orpheline') as any,
              'line-dasharray': motifDe('emprise_orpheline') as any,
              'line-opacity': 0.95,
            },
          },
          {
            /**
             * LE BÂTI RELEVÉ — hauteur mesurée ou nombre de niveaux saisi.
             * Teinte claire et pleine : c'est une donnée du registre.
             */
            id: 'batiments-3d', type: 'fill-extrusion', source: 'cadastral', 'source-layer': 'batiments',
            minzoom: 15,
            filter: ['!', ['to-boolean', ['get', 'hauteur_presumee']]],
            paint: {
              'fill-extrusion-color': ['interpolate', ['linear'], ['coalesce', ['get', 'hauteur_rendu'], 3],
                3, P.volumePresume, 12, P.volumeReleve, 30, P.volumeReleve],
              'fill-extrusion-height': ['coalesce', ['get', 'hauteur_rendu'], 3],
              'fill-extrusion-base': 0,
              'fill-extrusion-opacity': 1,
              'fill-extrusion-vertical-gradient': true,
            },
          },
          {
            /**
             * ⚠️⚠️ LE BÂTI PRÉSUMÉ — EXTRUDÉ, MAIS JAMAIS CONFONDU AVEC DU RELEVÉ.
             *
             * Décision du fondateur du 18/09 : tout le bâti est mis en volume
             * dès maintenant, la vraie taille se saisira depuis Cadastral.
             * J'avais objecté qu'une hauteur inventée est indiscernable d'une
             * hauteur mesurée ; la décision tient, et c'est cette couche qui
             * répond à l'objection plutôt que de l'ignorer.
             *
             * Teinte sourde et opacité moindre : au 18/09/2026, ZÉRO des
             * 1 744 535 emprises connaît sa hauteur, donc toute la ville est
             * dans cet état. À mesure que le recensement saisit les niveaux,
             * les volumes passent dans la couche du dessus et s'éclaircissent —
             * l'avancement du relevé se LIT sur la carte, sans tableau de bord.
             */
            id: 'batiments-3d-presume', type: 'fill-extrusion', source: 'cadastral', 'source-layer': 'batiments',
            minzoom: 15,
            filter: ['to-boolean', ['get', 'hauteur_presumee']],
            paint: {
              /**
               * ⚠️⚠️ L'OPACITÉ ÉTAIT À 0,72, ET C'ÉTAIT LA CAUSE DU FLOU.
               * Un volume translucide laisse voir CEUX DE DERRIÈRE : sur un
               * quartier dense, chaque toit se superpose à trois autres et
               * plus aucune emprise n'a de bord franc. Signalé par le
               * fondateur le 19-09-2026 — « trop flou, il faut beaucoup plus
               * de netteté ». La transparence disait « présumé » ; c'est la
               * TEINTE qui le dit, pas le fait de voir à travers.
               */
              'fill-extrusion-color': P.volumeMur,
              'fill-extrusion-height': ['coalesce', ['get', 'hauteur_rendu'], 3.5],
              'fill-extrusion-base': 0,
              'fill-extrusion-opacity': 0.97,
              // Le dégradé vertical assombrit le pied du volume : sans lui,
              // deux bâtiments mitoyens forment un seul aplat sans arête.
              'fill-extrusion-vertical-gradient': true,
            },
          },
          {
            /**
             * §5/CC§10 — LA SURFACE QUI REÇOIT LE CLIC SUR UNE SECTION.
             *
             * ⚠️⚠️ UNE COUCHE `line` N'EST CLIQUABLE QUE SUR SON TRAIT, à un ou
             * deux pixels près. Les contours de section existent depuis le
             * 19-09 et l'agent ne pouvait rien sélectionner : il visait une
             * section grande comme l'écran et ne touchait rien, sauf à poser
             * le curseur exactement sur la ligne.
             *
             * ⚠️ OPACITÉ QUASI NULLE, PAS `visibility: 'none'` : une couche
             * masquée n'est pas interrogée par `queryRenderedFeatures`. Elle
             * doit être RENDUE pour être cliquable — invisible à l'œil,
             * présente au clic.
             *
             * ⚠️ DÉCLARÉE AVANT LE PARCELLAIRE : elle ne doit teinter ni les
             * parcelles ni le bâti.
             */
            id: 'sections-surface', type: 'fill', source: 'cadastral', 'source-layer': 'sections',
            minzoom: 11,
            paint: { 'fill-color': P.section, 'fill-opacity': 0.012 },
          },
          {
            id: 'parcelles-remplissage', type: 'fill', source: 'cadastral', 'source-layer': 'parcelles',
            // ⚠️ 0,28 SUR CHAQUE PARCELLE FAIT UN VOILE CONTINU quand elles
            // se touchent toutes : le remplissage mangeait le contour qu'il
            // était censé accompagner. Le statut se lit au trait, pas à
            // l'aplat.
            paint: { 'fill-color': couleurLimite as any, 'fill-opacity': 0.16 },
          },
          /**
           * ⚠️⚠️ LES QUATRE ÉTATS PAR TRAME, PAS SEULEMENT PAR TEINTE — D.947.
           * Table unique `trames-etat.ts`, lue aussi par la légende. Le PLEIN
           * est la trame de l'état inscrit : pas de couche pour lui. Trois
           * couches FILTRÉES, jamais une expression : même règle que
           * `line-dasharray` (voir signatures-plan.ts), un `fill-pattern`
           * piloté par les données est hors contrat de confiance ici.
           */
          {
            id: 'parcelles-trame-instruction', type: 'fill', source: 'cadastral', 'source-layer': 'parcelles',
            filter: ['==', ['get', 'statut'], 'en_instruction'],
            paint: { 'fill-pattern': nomImageTrame('hachures'), 'fill-opacity': 0.55 },
          },
          {
            id: 'parcelles-trame-suspendue', type: 'fill', source: 'cadastral', 'source-layer': 'parcelles',
            filter: ['==', ['get', 'statut'], 'suspendue'],
            paint: { 'fill-pattern': nomImageTrame('croisillons'), 'fill-opacity': 0.55 },
          },
          {
            // ⚠️ Faible : 99,9996 % du plan est en projet — la trame doit se
            // lire de près sans faire un voile national de loin.
            id: 'parcelles-trame-projet', type: 'fill', source: 'cadastral', 'source-layer': 'parcelles',
            filter: ['==', ['get', 'statut'], 'projet'],
            paint: { 'fill-pattern': nomImageTrame('points'), 'fill-opacity': 0.3 },
          },
          {
            id: 'parcelles-contour', type: 'line', source: 'cadastral', 'source-layer': 'parcelles',
            // La largeur suit le zoom : un trait fixe de 1,2 px bave à z18 et
            // disparaît à z13.
            paint: {
              'line-color': couleurLimite as any,
              'line-width': expressionLargeur('parcelle') as any,
              'line-opacity': 0.9,
            },
          },
          {
            id: 'parcelle-selection', type: 'line', source: 'cadastral', 'source-layer': 'parcelles',
            filter: ['==', ['get', 'identifiant'], '—'],
            paint: { 'line-color': P.choix,
                     'line-width': expressionLargeur('choix_parcelle') as any },
          },
          {
            /* ⚠️ LA SÉLECTION DE LA TABLE SE VOIT — D.948. Sans ce trait, une
               ligne retenue par erreur partait dans l'export sans qu'aucune
               surbrillance ne la montre. Tireté : la parcelle OUVERTE reste
               le trait plein. */
            id: 'parcelles-table-selection', type: 'line', source: 'cadastral', 'source-layer': 'parcelles',
            filter: ['in', ['get', 'identifiant'], ['literal', []]],
            paint: { 'line-color': P.choix,
                     'line-width': expressionLargeur('choix_table') as any,
                     'line-dasharray': motifDe('choix_table') as any },
          },
          /**
           * ⚠️⚠️ LES DIVISIONS N'AVAIENT AUCUNE ÉTIQUETTE. La tuile porte leur
           * `nom` depuis toujours — huit régions, trente-quatre préfectures —
           * et le style ne l'écrivait nulle part : deux couches seulement, un
           * remplissage et un contour, tous deux en gris très sombre. Un agent
           * ne pouvait donc lire sur la carte NI dans quelle préfecture il se
           * trouve, ni où passe la limite. Signalé par le fondateur.
           *
           * ⚠️ DEUX COUCHES, PAS UNE, ET LA RAISON EST LE NOMBRE. 462 divisions
           * sur quatre niveaux, dont 396 quartiers : une couche unique les
           * afficherait toutes au même zoom et MapLibre en écarterait la
           * quasi-totalité par collision — il resterait quelques noms tirés au
           * hasard, dont on croirait que ce sont les seuls connus.
           *
           * ⚠️ ET ELLES SONT DÉCLARÉES EN DERNIER. MapLibre dessine dans
           * l'ordre : une étiquette posée avant les remplissages passerait SOUS
           * la couche des parcelles. Le texte existait, il était simplement
           * recouvert — un défaut qui se diagnostique dans la police alors
           * qu'il est dans l'ordre des couches.
           *
           * ⚠️⚠️ ELLES LISENT `divisions_noms`, PAS `divisions`. La première
           * version pointait sur la couche des POLYGONES, et MapLibre étiquette
           * chaque morceau qu'il reçoit : une préfecture qui s'étend sur six
           * tuiles s'écrivait six fois. Mesuré au navigateur — « FARANAH »
           * apparaissait six fois sur un même écran à z10. Le service sert
           * désormais UN point d'ancrage par division ; le nom ne peut donc
           * plus être écrit deux fois, quelle que soit la taille du territoire.
           */
          /**
           * §4/§5 — LA LIMITE DE SECTION, LUE D'UN COUP D'ŒIL.
           *
           * ⚠️⚠️ CES DEUX COUCHES ÉTAIENT DÉCLARÉES AVANT LE PARCELLAIRE, ET
           * ON NE LES VOYAIT PAS. MapLibre dessine dans l'ordre : 7 731
           * parcelles recouvraient le contour de section trait pour trait.
           * J'avais écrit « le contour de section doit passer SOUS le
           * parcellaire » — c'était le mauvais choix, et il ne se constate
           * qu'à l'écran : le style était valide, la couche présente dans la
           * tuile, et la limite invisible. Signalé par le fondateur le
           * 19-09-2026, capture à l'appui.
           *
           * ⚠️ UNE SECTION N'EST PAS UNE PARCELLE : c'est le découpage qui les
           * contient. Elle se lit donc PAR-DESSUS, comme la limite
           * administrative — la hiérarchie du dessin doit suivre celle du
           * droit, pas l'inverse.
           *
           * ⚠️ LA TEINTE EST ABSENTE DU RESTE DE LA CARTE, délibérément : l'or
           * dit la parcelle inscrite et la voirie structurante, le bleu
           * l'instruction, le rouge la suspension, le gris la maille et le
           * bâti. Un turquoise n'entre en collision avec aucun de ces sens.
           */
          {
            id: 'sections-contour-voie', type: 'line', source: 'cadastral', 'source-layer': 'sections',
            minzoom: 11,
            filter: ['==', ['get', 'origine'], 'voie'],
            paint: {
              'line-color': P.section,
              'line-width': expressionLargeur('section_bornee') as any,
              'line-opacity': 0.9,
            },
          },
          {
            // Le carroyage : tireté, parce qu'il n'est PAS une limite du
            // terrain — 152 442 sections sur 153 248 sont dans ce cas, et les
            // dessiner du même trait laisserait croire à un découpage réel.
            id: 'sections-contour-grille', type: 'line', source: 'cadastral', 'source-layer': 'sections',
            minzoom: 11,
            filter: ['!=', ['get', 'origine'], 'voie'],
            paint: {
              'line-color': P.section,
              'line-width': expressionLargeur('section_carroyage') as any,
              'line-dasharray': motifDe('section_carroyage') as any,
              'line-opacity': 0.72,
            },
          },
          /**
           * ⚠️ LA SECTION OUVERTE EST SOULIGNÉE — et le filtre porte sur `id`,
           * JAMAIS sur `code`. 1 515 codes sur 1 552 sont portés par plusieurs
           * collectivités : un filtre par code allumerait une centaine de
           * sections à travers le pays en paraissant n'en désigner qu'une.
           */
          {
            id: 'section-active', type: 'line', source: 'cadastral', 'source-layer': 'sections',
            minzoom: 11,
            filter: ['==', ['get', 'id'], '—'],
            paint: {
              'line-color': jetonCarte('--carte-choix', '#ffffff'),
              'line-width': expressionLargeur('choix_section') as any,
              'line-opacity': 0.95,
            },
          },
          {
            id: 'divisions-noms-hautes', type: 'symbol', source: 'cadastral', 'source-layer': 'divisions_noms',
            minzoom: 4, maxzoom: 13,
            filter: ['all', ['has', 'nom'], ['<=', ['get', 'niveau'], 2]],
            layout: {
              'text-field': ['get', 'nom'],
              'text-font': ['Noto Sans Regular'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 5, 11, 11, 15],
              // L'espacement et la capitale disent « administratif » sans
              // ajouter un mot : on ne confond pas une préfecture avec une rue.
              'text-transform': 'uppercase',
              'text-letter-spacing': 0.18,
              'text-padding': 6,
              'text-max-width': 9,
            },
            paint: {
              'text-color': jetonCarte('--carte-etiquette', '#d0d0d0'),
              'text-halo-color': jetonCarte('--carte-halo', '#000000'),
              'text-halo-width': 1.8,
            },
          },
          {
            id: 'divisions-noms-basses', type: 'symbol', source: 'cadastral', 'source-layer': 'divisions_noms',
            minzoom: 12,
            filter: ['all', ['has', 'nom'], ['>=', ['get', 'niveau'], 3]],
            layout: {
              'text-field': ['get', 'nom'],
              'text-font': ['Noto Sans Regular'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 17, 13],
              'text-padding': 4,
              'text-max-width': 10,
            },
            paint: {
              'text-color': jetonCarte('--carte-etiquette-faible', '#8a8a8a'),
              'text-halo-color': jetonCarte('--carte-halo', '#000000'),
              'text-halo-width': 1.6,
            },
          },
          /**
           * ⚠️ LE NOM DE RUE, ÉCRIT LE LONG DE LA VOIE. Il n'était lisible qu'au
           * survol faute de glyphes — donc invisible à qui imprime, à qui montre
           * l'écran à quelqu'un, et à qui cherche une rue par son nom.
           *
           * ⚠️ `symbol-placement: line` ET NON `point` : une rue n'a pas de
           * centre, elle a une longueur. Posée en point, l'étiquette atterrit au
           * milieu d'un tronçon arbitraire et se répète à chaque segment.
           *
           * ⚠️ AFFICHÉES SEULEMENT À PARTIR DE z14. Plus tôt, MapLibre écarte la
           * quasi-totalité des libellés faute de place et le résultat est pire
           * qu'une carte sans nom : quelques rues nommées au hasard, dont on
           * croirait que ce sont les seules connues.
           */
          /**
           * ⚠️ UN NOM PAR COLLECTIVITÉ, PAS UN PAR TUILE TRAVERSÉE. L'ancre est
           * calculée une seule fois, à la matérialisation (`ST_PointOnSurface`)
           * — c'est le défaut « FARANAH six fois » corrigé le 12/09 sur la
           * couche jumelle, et un centroïde de commune en croissant tombe hors
           * d'elle-même.
           *
           * ⚠️ ELLE DIT CE QUE LA COLLECTIVITÉ PORTE. « Matoto » seul ne
           * distingue pas une commune cadastrée d'une commune vide, et 81 %
           * des sections du pays ne portent aucune parcelle : sans le second
           * membre, l'agent ouvre au hasard.
           */
          {
            id: 'collectivites-noms', type: 'symbol', source: 'cadastral',
            'source-layer': 'collectivites_noms',
            minzoom: 6, maxzoom: 13,
            layout: {
              'text-field': [
                'case', ['>', ['get', 'parcelles'], 0],
                ['concat', ['get', 'nom'], '\n', ['to-string', ['get', 'sections']], ' sections'],
                ['concat', ['get', 'nom'], '\naucune parcelle'],
              ],
              'text-font': ['Noto Sans Regular'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 6, 10, 11, 14],
              'text-letter-spacing': 0.06,
              'text-line-height': 1.25,
              'text-allow-overlap': false,
              'text-padding': 6,
            },
            paint: {
              'text-color': jetonCarte('--carte-collectivite', '#86dcec'),
              'text-halo-color': jetonCarte('--carte-halo', '#000000'),
              'text-halo-width': 1.6,
              'text-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.75, 9, 1, 12, 0.9, 13, 0],
            },
          },
          {
            id: 'rues-noms', type: 'symbol', source: 'cadastral', 'source-layer': 'rues',
            /**
             * ⚠️ 14 → 13, ET LE SEUIL EST SERVI, PLUS RECOPIÉ. À z13,5 — le
             * zoom naturel pour lire un quartier — la carte n'affichait aucun
             * nom, et l'agent n'avait aucun moyen de savoir qu'il lui suffisait
             * de zoomer d'un demi-cran. Signalé par le fondateur.
             * Deux tables du même seuil divergeraient : celle-ci suit désormais
             * `zoomNomsDeRue` rendu par `/tuiles/reglages`.
             */
            minzoom: zoomNomsDeRue ?? 13,
            filter: ['all', ['has', 'nom'], ['!=', ['get', 'nom'], '']],
            layout: {
              'text-field': ['get', 'nom'],
              'text-font': ['Noto Sans Regular'],
              'symbol-placement': 'line',
              'text-size': ['interpolate', ['linear'], ['zoom'], 14, 10, 18, 13],
              'text-max-angle': 38,
              'text-padding': 3,
              'symbol-spacing': 260,
            },
            paint: {
              'text-color': jetonCarte('--carte-etiquette', '#d0d0d0'),
              // Halo sombre : sans lui le libellé disparaît dès qu'il croise
              // une emprise bâtie claire.
              'text-halo-color': jetonCarte('--carte-halo', '#000000'),
              'text-halo-width': 1.6,
            },
          },
          {
            /**
             * ⚠️ LIT `sections_noms`, PAS `sections` — un code par section, pas
             * un par tuile traversée. Le défaut corrigé sur les divisions le
             * 12/09 (« FARANAH » six fois à l'écran) se reproduirait à
             * l'identique sur 153 248 objets si l'étiquette pointait sur les
             * polygones.
             *
             * ⚠️⚠️ DÉCLARÉE APRÈS `rues-noms`, ET C'EST L'INVERSE DE L'ORDRE
             * NATUREL. MapLibre place les symboles dans l'ordre des couches et
             * la PREMIÈRE servie gagne la collision : déclarés avant, les codes
             * de section évinçaient les noms de rues. Or un plan cadastral sans
             * nom de rue ne se lit pas — c'est par la rue qu'un agent se repère
             * avant de lire un code. Demande du fondateur, 19-09-2026 : les noms
             * de rues et de routes restent visibles EN MÊME TEMPS que les
             * sections. Le code de section cède, jamais la rue.
             */
            id: 'sections-codes', type: 'symbol', source: 'cadastral', 'source-layer': 'sections_noms',
            minzoom: 12,
            layout: {
              'text-field': ['get', 'code'] as any,
              'text-font': ['Noto Sans Regular'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 12, 9, 15, 12] as any,
              'text-padding': 4,
            },
            paint: {
              'text-color': ['match', ['get', 'origine'], 'voie', P.etiquette, P.etiquetteFaible] as any,
              'text-halo-color': P.halo,
              'text-halo-width': 1.2,
            },
          },
        ],
      },
      center: VUE_INITIALE.centre,
      zoom: VUE_INITIALE.zoom,
      // ⚠️ COUPÉ ET REMPLACÉ, JAMAIS COUPÉ TOUT COURT. Le contrôle natif de
      // MapLibre ne lit que les `attribution` des sources d'un style ; nos
      // couches viennent d'une source unique dont la provenance réelle est
      // en base, pas dans le style. Il aurait donc crédité personne, ou
      // n'importe qui. Le nôtre est calculé depuis `src/partage/sources.ts`,
      // et un essai refuse que cette ligne existe sans son remplaçant.
      attributionControl: false,
    });

    c.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    /**
     * ⚠️ LE RELIEF EST OPTIONNEL ET ÉTEINT PAR DÉFAUT, et ce n'est pas de la
     * prudence : un plan cadastral se lit à plat. Le relief sert à COMPRENDRE
     * un terrain, pas à instruire un dossier — une parcelle vue en oblique
     * change de forme apparente, et c'est exactement ce qu'un plan ne doit
     * jamais faire. On l'allume quand on en a besoin.
     */
    c.addControl(new maplibregl.TerrainControl({ source: 'releve', exaggeration: 1.4 }), 'top-right');
    c.addControl(new maplibregl.ScaleControl({ maxWidth: 140, unit: 'metric' }), 'bottom-left');

    // ⚠️ UNE TUILE QUI ÉCHOUE DOIT SE VOIR. Sans cela, une carte vide par
    // erreur 401/503 est indistinguable d'une zone sans données — et l'agent
    // conclut « il n'y a rien ici » sur une panne d'authentification.
    c.on('error', (e: any) => {
      const statut = e?.error?.status;
      if (statut === 401) setErreurTuiles("Session expirée — les tuiles sont refusées (401).");
      else if (statut === 403) setErreurTuiles("Votre habilitation ne couvre pas cette zone (403).");
      else if (statut >= 500) setErreurTuiles(`Le service de tuiles répond ${statut}.`);
      else if (e?.error?.message) setErreurTuiles(e.error.message);
    });
    c.on('sourcedata', (e: any) => { if (e.isSourceLoaded && e.sourceId === 'cadastral') setErreurTuiles(null); });
    /**
     * ⚠️ LES IMAGES DE TRAME SE POSENT À LA DEMANDE — D.947. Les déclarer
     * après `load` laisserait une fenêtre où le style les référence sans les
     * trouver : MapLibre peindrait les aplats SANS trame, sans un mot.
     * `styleimagemissing` ferme cette fenêtre, quel que soit l'ordre.
     */
    c.on('styleimagemissing', (e: { id: string }) => {
      if (!e.id.startsWith('trame-')) return;
      poserImagesTrames(c, {
        hachures: P.statut.enInstruction,
        croisillons: P.statut.suspendue,
        points: P.maille,
      });
    });

    // ⚠️ `originalEvent` DISTINGUE LE GESTE DU PROGRAMME. Sans lui, notre propre
    // `jumpTo` marquerait la carte comme « bougée par l'agent » et se
    // désarmerait lui-même — un recentrage qui s'annule au premier essai.
    c.on('movestart', (e: any) => { if (e.originalEvent) aDejaBouge.current = true; });

    c.on('move', () => { setZoom(Number(c.getZoom().toFixed(1))); });

    /**
     * LE SURVOL COMPLÈTE L'ÉTIQUETTE, IL NE LA REMPLACE PLUS.
     *
     * ⚠️ CE COMMENTAIRE AFFIRMAIT QUE LE STYLE N'AVAIT « DÉLIBÉRÉMENT PAS » DE
     * SERVEUR DE GLYPHES, et que le survol était donc le seul recours. C'était
     * faux depuis l'ajout de `glyphs` plus haut — et la couche `rues-noms`
     * existait juste au-dessus. Un commentaire qui décrit un état révolu se
     * recopie de bonne foi : il a servi à conclure, pendant des jours, que
     * l'absence de noms était un choix.
     *
     * Ce que le survol apporte en propre : le TYPE de voie et le sens unique,
     * qu'aucune étiquette ne porte.
     */
    const bulleRue = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 8 });
    c.on('mousemove', 'rues', (e) => {
      const f = e.features?.[0];
      if (!f) return;
      c.getCanvas().style.cursor = 'crosshair';
      const p = f.properties as { nom?: string; type_voie?: string; sens_unique?: boolean };
      const texteVoie =
        `${p.nom ?? 'voie sans nom'}${p.type_voie ? ` · ${p.type_voie}` : ''}${p.sens_unique ? ' · sens unique' : ''}`;
      bulleRue.setLngLat(e.lngLat).setText(texteVoie).addTo(c);
      refVoieSurvolee.current?.(texteVoie);
    });
    c.on('mouseleave', 'rues', () => {
      c.getCanvas().style.cursor = ''; bulleRue.remove(); refVoieSurvolee.current?.(null);
    });

    /**
     * LA COLLECTIVITÉ SOUS LE CENTRE DE L'ÉCRAN.
     *
     * ⚠️⚠️ « RIEN AU CENTRE » N'EST PAS « HORS DE TOUTE COLLECTIVITÉ ».
     * `queryRenderedFeatures` ne voit que ce qui est DÉJÀ peint : appelée sur
     * `moveend`, elle rend un tableau vide pendant que les tuiles arrivent.
     * Effacer le panneau sur ce vide-là le ferait clignoter à chaque
     * déplacement, et l'agent lirait « aucune commune ici » sur une commune.
     * On ne CONCLUT donc à l'absence que sur `idle` — l'événement qui dit que
     * le rendu est terminé. `moveend` ne peut que confirmer une présence.
     */
    /** Les propriétés d'une tuile de collectivité, mises au contrat. */
    const collectiviteDepuis = (p: Record<string, unknown>): CollectiviteCarte => ({
      sourceId: Number(p.source_id),
      nom: String(p.nom ?? ''),
      code: p.code == null || p.code === '' ? null : String(p.code),
      echelon: String(p.echelon ?? ''),
      sections: Number(p.sections ?? 0),
      parcelles: Number(p.parcelles ?? 0),
      gelees: Number(p.gelees ?? 0),
      /** ⚠️ MVT rend les booléens tels quels, mais une tuile plus ancienne
       *  peut servir 0/1 : on normalise plutôt que de supposer. */
      peuplee: p.peuplee === true || p.peuplee === 1 || p.peuplee === 'true',
    });
    /**
     * ⚠️ CETTE LECTURE NE POSE PLUS AUCUNE SÉLECTION — item D.997. Elle
     * alimentait le panneau de droite, qui changeait donc au simple
     * défilement. Elle ne nourrit plus que `position`, c'est-à-dire la barre
     * d'état : « vous êtes sur » n'est pas « vous avez choisi ».
     */
    const lireCollectivite = (conclure: boolean) => {
      const centre = c.project(c.getCenter());
      const f = c.queryRenderedFeatures(centre, { layers: ['collectivites-remplissage'] })[0];
      if (!f) { if (conclure) setCollectivitePosition(null); return; }
      setCollectivitePosition(collectiviteDepuis(f.properties ?? {}));
    };
    c.on('moveend', () => lireCollectivite(false));
    c.on('idle', () => lireCollectivite(true));

    const cliquables = ['parcelles-remplissage', 'batiments', 'sections-surface'];
    for (const couche of cliquables) {
      c.on('mouseenter', couche, () => { c.getCanvas().style.cursor = 'pointer'; });
      c.on('mouseleave', couche, () => { c.getCanvas().style.cursor = ''; });
    }

    c.on('click', (e) => {
      /**
       * ⚠️ LE TRACÉ SUSPEND LA SÉLECTION, ET C'EST LE SEUL MOYEN SÛR. MapLibre
       * appelle les écouteurs dans leur ORDRE D'INSCRIPTION : celui-ci est posé
       * au montage de la carte, donc AVANT ceux que l'outil de tracé ajoutera.
       * Sans ce garde, poser un sommet ouvrirait en même temps la fiche de la
       * parcelle survolée — l'agent verrait son panneau changer sous lui à
       * chaque clic, et conclurait que le tracé fait n'importe quoi.
       *
       * Le drapeau est lu dans un `ref` : cet effet ne se rejoue jamais (il
       * porte `if (carte.current) return`), donc une valeur capturée à la
       * fermeture resterait celle du premier montage — c'est-à-dire toujours
       * « non suspendu ».
       */
      if (refSuspendu.current) return;
      const trouvees = c.queryRenderedFeatures(e.point, { layers: cliquables }) as MapGeoJSONFeature[];
      /**
       * ⚠️ L'ORDRE DE PRIORITÉ N'EST PAS UN GOÛT, C'EST LA RÈGLE DU ZOOM.
       * La parcelle prime sur le bâtiment : le clic d'un agent cadastral porte
       * sur l'objet foncier, le bâti n'est qu'un indice de terrain. La SECTION
       * vient en dernier — non qu'elle compte moins, mais parce qu'elle couvre
       * tout : placée devant, elle rendrait parcelle et bâtiment inatteignables
       * partout où les trois se superposent.
       *
       * Sous z13 aucune parcelle n'est servie : le clic y désigne donc
       * forcément une section, sans règle supplémentaire. La cascade du
       * contrat tombe juste d'elle-même.
       */
      const parcelle = trouvees.find((f) => f.sourceLayer === 'parcelles');
      const batiment = trouvees.find((f) => f.sourceLayer === 'batiments');
      const section = trouvees.find((f) => f.sourceLayer === 'sections');
      /**
       * ⚠️ LA SECTION SE RETIENT MÊME QUAND LE CLIC PORTE SUR UNE PARCELLE.
       * La cascade est collectivité → section → parcelle : ouvrir une parcelle
       * sans retenir la section qui la contient ferait remonter d'un cran le
       * fil d'Ariane à chaque clic, et l'agent perdrait le tableau
       * d'assemblage qu'il était en train de lire.
       */
      /**
       * ⚠️⚠️ LE POINT DU CLIC DONNE LA PILE ENTIÈRE — item D.997. Le panneau
       * ne suivant plus le centre de l'écran, la collectivité doit se choisir
       * AU CLIC comme le reste. Le polygone de la collectivité contient ce
       * qu'on y clique : la lire au même point adopte le bon parent, y compris
       * quand la section cliquée est au bord de l'écran, dans une commune que
       * le centre ne regarde pas — le cas exact où l'ancienne lecture mentait.
       */
      const collTuile = c.queryRenderedFeatures(e.point,
        { layers: ['collectivites-remplissage'] })[0];
      const collAuPoint = collTuile ? collectiviteDepuis(collTuile.properties ?? {}) : null;

      if (section) {
        const sp = section.properties ?? {};
        setSectionChoisie({
          id: String(sp.id ?? ''),
          code: String(sp.code ?? ''),
          collectivite: Number(sp.collectivite),
          origine: sp.origine === 'voie' ? 'voie' : 'grille',
          surfaceM2: Number(sp.surface_m2 ?? 0),
        });
        if (collAuPoint) setCollectiviteChoisie(collAuPoint);
      } else if (!parcelle && !batiment) {
        // Un clic hors de toute section referme la section ; s'il tombe sur
        // une collectivité, il la CHOISIT — c'est désormais le seul chemin
        // par la carte pour ouvrir un tableau d'assemblage, et c'est un geste.
        setSectionChoisie(null);
        setCollectiviteChoisie(collAuPoint);
      } else if (collAuPoint) {
        // Parcelle ou bâtiment cliqué hors section servie : le parent suit.
        setCollectiviteChoisie(collAuPoint);
      }

      const choisie = parcelle ?? batiment ?? section;
      if (!choisie) { onSelection(null); return; }
      onSelection({
        couche: choisie.sourceLayer as SelectionCarte['couche'],
        /**
         * ⚠️⚠️ POUR UNE SECTION, C'EST `id` — SURTOUT PAS `code`.
         * J'avais écrit `code` ce matin, et c'était faux : mesuré le
         * 21-09-2026, **1 515 codes sur 1 552 sont portés par plusieurs
         * collectivités**, et `AAA` existe dans les 331. Le code désignait
         * donc une centaine de sections à la fois, en paraissant en désigner
         * une seule — l'erreur ne se voit pas, elle ouvre simplement la
         * mauvaise section.
         *
         * `properties.collectivite` accompagne le code : c'est le couple
         * (code, collectivité) qu'attendent les routes `/sections/:code`, et
         * `id` qui identifie sans ambiguïté.
         */
        identifiant: String(
          choisie.sourceLayer === 'sections'
            ? (choisie.properties?.id ?? '')
            : (choisie.properties?.identifiant ?? ''),
        ),
        proprietes: choisie.properties ?? {},
      });
    });

    carte.current = c;
    refOnCarte.current?.(c);
    /* PC§27 — l'affichage courant s'applique dès le bâti de la carte : une
       reconstruction (bascule de thème) repartirait sinon sur la vue brute,
       bâti rallumé, pendant que le commutateur affiche encore « Cadastral ». */
    appliquerAffichage(c, modeAffichage(refAffichage.current));
    setCartePrete(true);   // débloque le recentrage : voir l'effet plus bas

    /**
     * ⚠️⚠️ LA TOILE SUIT SON CONTENEUR — ET SANS CECI, ELLE NE LE SUIVAIT PAS.
     *
     * Signalé par le fondateur sur une capture : la carte ne remplissait que la
     * moitié haute de la scène, le reste en blanc. Le CSS était pourtant juste —
     * `html/body/#racine` à 100 %, `.poste` en colonne, `.corps` et `.scene` en
     * `flex: 1`, `.carte-cadastrale` en `inset: 0`. Toute la chaîne de hauteurs
     * vérifiée ligne à ligne : rien à corriger dedans.
     *
     * La cause est ailleurs : **MapLibre mesure son conteneur UNE SEULE FOIS**,
     * à la construction. Il pose bien un écouteur sur le redimensionnement de la
     * FENÊTRE — d'où le fait que réduire puis agrandir la fenêtre « répare » la
     * carte, ce qui fait chercher un défaut de CSS — mais il n'observe JAMAIS son
     * propre conteneur. Or ici le conteneur n'a pas sa hauteur finale au moment
     * du `new maplibregl.Map()` : `.carte-cadastrale` est une grille dont les
     * colonnes dépendent de `:has(.panneau-cadastral.replie)`, et le panneau se
     * monte après. La toile garde donc la taille périmée du premier instant.
     *
     * ⚠️ ET LE SYMPTÔME NE RESSEMBLE PAS À SA CAUSE : une toile trop courte se lit
     * « la mise en page est cassée », on va donc lire le CSS — qui est correct.
     * C'est le motif déjà payé ici plusieurs fois, l'erreur qui pointe ailleurs.
     *
     * `ResizeObserver` observe le conteneur lui-même : tout changement de sa boîte
     * — montage d'un panneau, repli, bascule de style, fenêtre — appelle
     * `resize()`. Il est débranché avec la carte, sinon il survit au démontage et
     * appelle `resize()` sur une carte détruite.
     */
    const suivi = new ResizeObserver(() => { c.resize(); });
    suivi.observe(conteneur.current);
    /* Et un premier appel après le rendu courant : l'observateur ne se déclenche
     * qu'au PROCHAIN changement de boîte, pas sur l'état initial déjà périmé. */
    const premier = requestAnimationFrame(() => { c.resize(); });

    return () => {
      suivi.disconnect();
      cancelAnimationFrame(premier);
      /* ⚠️ PRÉVENIR AVANT `c.remove()`. Le parent doit pouvoir retirer ses
         couches et ses écouteurs tant que la carte existe encore ; appelé
         après, il travaillerait sur une carte détruite et lèverait. */
      refOnCarte.current?.(null);
      c.remove();
      carte.current = null;
    };
  }, [onSelection, theme]);

  /**
   * LA CARTE SUIT L'ÉDITION — le gabarit change, les tuiles sont redemandées.
   *
   * ⚠️ `setTiles` PLUTÔT QUE RETIRER ET REPOSER LA SOURCE. Retirer une source
   * oblige à retirer d'abord les vingt et une couches qui s'y appuient, puis à
   * les remettre dans le bon ordre : une seule oubliée, et le plan se dessine
   * sens dessus dessous. MapLibre sait remplacer les URL en place et purge son
   * cache de tuiles tout seul.
   *
   * ⚠️ ET ON NE FAIT RIEN AU PREMIER RENDU : le gabarit y est déjà posé par la
   * construction du style. Rappeler `setTiles` avec la même valeur purgerait
   * le cache pour rien, à chaque rendu du parent.
   */
  const gabaritPose = useRef<string | undefined>(undefined);
  useEffect(() => {
    const c = carte.current;
    if (!c || !gabaritTuiles) return;
    if (gabaritPose.current === undefined) { gabaritPose.current = gabaritTuiles; return; }
    if (gabaritPose.current === gabaritTuiles) return;
    gabaritPose.current = gabaritTuiles;
    const src = c.getSource('cadastral') as { setTiles?: (u: string[]) => void } | undefined;
    /* Un repli explicite : si la version de MapLibre ne sait pas remplacer les
       URL, on ne casse pas la carte — elle reste simplement à son cache de
       cinq minutes, ce que l'ancien comportement faisait déjà. */
    if (typeof src?.setTiles === 'function') {
      src.setTiles([`${window.location.origin}${gabaritTuiles}`]);
    }
  }, [gabaritTuiles]);

  /**
   * ⚠️ ON RECENTRE UNE SEULE FOIS, ET JAMAIS SI L'AGENT A DÉJÀ BOUGÉ. La vue
   * conseillée arrive en asynchrone, après la création de la carte : sans le
   * garde `aDejaBouge`, elle arracherait l'écran sous les doigts de quelqu'un
   * en train de naviguer — plusieurs secondes après son geste, donc sans qu'il
   * puisse relier les deux. Un recentrage qu'on n'a pas demandé se lit comme un
   * bug, même quand la destination est la bonne.
   */
  const recentrageFait = useRef(false);
  const [cartePrete, setCartePrete] = useState(false);
  useEffect(() => {
    const c = carte.current;
    /**
     * ⚠️ CET EFFET NE DÉPENDAIT QUE DE `vueConseillee`, ET POUVAIT NE JAMAIS
     * S'EXÉCUTER. Si les réglages arrivaient avant que la carte ne soit créée,
     * `carte.current` valait `null`, l'effet sortait — et ne se rejouait plus
     * jamais, puisque sa seule dépendance ne changeait plus. Un recentrage qui
     * marche « la plupart du temps » est un recentrage dont on ne peut rien
     * conclure quand il manque. `cartePrete` le fait dépendre des DEUX
     * conditions qu'il attend.
     */
    if (!c || !cartePrete || !vueConseillee || recentrageFait.current || aDejaBouge.current) return;
    recentrageFait.current = true;
    c.jumpTo({ center: vueConseillee.centre, zoom: vueConseillee.zoom });
  }, [vueConseillee, cartePrete]);

  /**
   * LES TROIS EFFETS DE LA CASCADE — ils vont ensemble et se lisent ensemble.
   */

  /**
   * ⚠️ L'EFFET QUI EFFAÇAIT LA SECTION AU DÉFILEMENT A ÉTÉ RETIRÉ — D.997.
   * Il comparait la section CHOISIE à la collectivité SOUS LE CENTRE : un
   * simple déplacement de carte détruisait donc une sélection faite au clic.
   * Son souci était réel — une section retenue sous le mauvais parent ferait
   * lire « AAA » d'une autre commune — mais la cause a disparu : la
   * collectivité choisie est désormais adoptée AU MÊME CLIC que la section
   * (le point du clic donne la pile entière), les deux ne peuvent plus
   * diverger par la navigation.
   */

  /**
   * ⚠️ LA COLLECTIVITÉ IMPOSÉE EST ADOPTÉE SUR SA CLÉ — même règle que la
   * section imposée, posée avant l'effet de remontée pour la même raison.
   * Choisir une collectivité referme la section : l'agent remonte d'un cran.
   */
  const cleCollectivite = collectiviteImposee?.cle ?? null;
  useEffect(() => {
    if (!collectiviteImposee) return;
    setCollectiviteChoisie(collectiviteImposee.collectivite);
    setSectionChoisie(null);
  }, [cleCollectivite]);   // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * ⚠️ LA SECTION IMPOSÉE EST ADOPTÉE SUR SA CLÉ, JAMAIS SUR SA VALEUR — et
   * cet effet est déclaré AVANT celui qui remonte le contexte, pour que le
   * parent reçoive l'état déjà adopté plutôt que celui du rendu précédent.
   */
  const cleSection = sectionImposee?.cle ?? null;
  useEffect(() => {
    if (!sectionImposee) return;
    setSectionChoisie(sectionImposee.section);
  }, [cleSection]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Ce que la carte regarde, remonté au parent — une seule source. */
  useEffect(() => {
    refContexte.current?.({
      zoom, collectivite: collectiviteChoisie, section: sectionChoisie,
      position: collectivitePosition,
    });
  }, [zoom, collectiviteChoisie, sectionChoisie, collectivitePosition]);

  /**
   * Les deux liserés de la cascade. Posés par filtre et non par couche
   * GeoJSON : la géométrie est déjà dans la tuile, la redemander en JSON
   * ferait une seconde source pour le même objet.
   */
  useEffect(() => {
    const c = carte.current;
    if (!c || !cartePrete) return;
    if (c.getLayer('collectivite-active')) {
      /* ⚠️ Le liseré suit la collectivité CHOISIE, pas celle sous le centre :
         un liseré qui suivait le défilement était la moitié visible de D.997. */
      c.setFilter('collectivite-active', ['==', ['get', 'source_id'], collectiviteChoisie?.sourceId ?? -1]);
    }
    if (c.getLayer('section-active')) {
      c.setFilter('section-active', ['==', ['get', 'id'], sectionChoisie?.id ?? '—']);
    }
  }, [collectiviteChoisie, sectionChoisie, cartePrete]);

  /** PC§27 — la bascule d'affichage s'applique À CHAUD, caméra préservée. */
  useEffect(() => {
    const c = carte.current;
    if (!c || !cartePrete) return;
    appliquerAffichage(c, modeAffichage(affichage));
  }, [affichage, cartePrete]);

  /**
   * ⚠️ LE PLAN (état/qualité) S'APPLIQUE À CHAUD, LUI AUSSI — revue du
   * 12/10/2026 : `couleurLimite` n'était lu qu'au LITTÉRAL de construction
   * (dépendances [onSelection, theme]) et `setPaintProperty` n'existait nulle
   * part : basculer le plan changeait la légende immédiatement, et la toile
   * seulement au prochain changement de thème. La légende était CRUE.
   * Les expressions sont LES MÊMES que celles du littéral (couleurLimite,
   * couleurBati) — deux lecteurs, une source.
   */
  useEffect(() => {
    const c = carte.current;
    if (!c || !cartePrete) return;
    if (c.getLayer('parcelles-remplissage')) {
      c.setPaintProperty('parcelles-remplissage', 'fill-color', couleurLimite as any);
    }
    if (c.getLayer('parcelles-contour')) {
      c.setPaintProperty('parcelles-contour', 'line-color', couleurLimite as any);
    }
    if (c.getLayer('batiments')) {
      c.setPaintProperty('batiments', 'fill-color', couleurBati);
    }
  }, [couleurLimite, couleurBati, cartePrete]);

  /**
   * ALLER OÙ UN PANNEAU LE DEMANDE.
   *
   * ⚠️ `jumpTo`, PAS `flyTo` — refus D.951, tranché par le fondateur : pas
   * d'animation de caméra sur le poste. Le `flyTo` qui vivait ici plaidait
   * l'orientation (« le vol dit le déplacement ») ; le poste dit désormais la
   * destination AUTREMENT : le fil d'Ariane nomme où l'on est, le liseré de la
   * collectivité choisie marque le cadre, et la barre d'état porte la position.
   * Une caméra qui vole pendant deux secondes est une carte qu'on ne peut ni
   * lire ni cliquer pendant deux secondes, cinquante fois par jour.
   *
   * ⚠️ ET IL MARQUE `aDejaBouge` : sans cela, la vue conseillée arrivant en
   * retard arracherait l'écran juste après le saut demandé.
   */
  const cleCible = cible?.cle ?? null;
  useEffect(() => {
    const c = carte.current;
    if (!c || !cartePrete || !cible) return;
    aDejaBouge.current = true;
    c.jumpTo({ center: cible.centre, zoom: cible.zoom });
  }, [cleCible, cartePrete]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Le liseré blanc suit la parcelle ouverte dans le panneau latéral.
  useEffect(() => {
    const c = carte.current;
    if (!c || !c.isStyleLoaded()) return;
    c.setFilter('parcelle-selection', ['==', ['get', 'identifiant'], parcelleActive ?? '—']);
  }, [parcelleActive]);

  // Le reflet de la TABLE — liste vide comprise : un reflet qui survit ment.
  useEffect(() => {
    const c = carte.current;
    if (!c || !c.isStyleLoaded()) return;
    if (!c.getLayer('parcelles-table-selection')) return;
    c.setFilter('parcelles-table-selection',
      ['in', ['get', 'identifiant'], ['literal', [...(selectionTable ?? [])]]]);
  }, [selectionTable]);

  /**
   * ⚠️ LE SEUIL DES NOMS EST APPLIQUÉ À LA COUCHE, PAS SEULEMENT À SA CRÉATION.
   * `minzoom` est lu une fois, au moment où le style est bâti — or les réglages
   * arrivent en asynchrone, souvent APRÈS. Les deux valeurs coïncident
   * aujourd'hui (13 des deux côtés), ce qui rend le défaut invisible : il
   * n'apparaîtrait qu'au premier réglage servi différent, et se manifesterait
   * par un bandeau annonçant un seuil que la carte n'applique pas.
   */
  useEffect(() => {
    const c = carte.current;
    if (!c || !cartePrete || zoomNomsDeRue === undefined) return;
    if (!c.getLayer('rues-noms')) return;
    c.setLayerZoomRange('rues-noms', zoomNomsDeRue, 24);
  }, [zoomNomsDeRue, cartePrete]);

  // ⚠️ LES COUCHES PRÉSENTES, PAS LES COUCHES DÉCLARÉES. Sous son seuil une
  // couche n'est pas servie : créditer sa source reviendrait à attribuer une
  // donnée qui n'est pas à l'écran.
  const couchesPresentes = [
    'divisions',
    // ⚠️ SERVIE DÈS z0 ET JUSQU'À z16, comme `divisions` : elle est le cadre
    // dans lequel tout le reste se lit, et son attribution est la même — ses
    // contours viennent de Maps, donc d'OSM, malgré son nom de registre d'État.
    'collectivites',
    ...(zoomNomsDeRue !== undefined || zoom >= 9 ? ['rues'] : []),
    ...(seuils && zoom >= seuils.parcelles ? ['parcelles'] : []),
    ...(seuils && zoom >= seuils.batiments ? ['batiments'] : []),
  ];

  return (
    <div className="carte-hote">
      {/**
        * ⚠️⚠️ LES BANDEAUX SONT DANS LE FLUX, PAS PAR-DESSUS LE PLAN — D.943.
        * L'alerte et le repère de couverture étaient posés en absolu sur la
        * toile : une alerte qui recouvre la carte cache précisément ce qu'elle
        * commente. Ici ils POUSSENT la toile au lieu de la couvrir — la carte
        * reste entière, et son centre désigne une surface qu'on voit en entier.
        */}
      <div className="carte-bandeaux">
      {/* ⚠️ « AUCUNE PARCELLE ICI » N'EST PAS « AUCUNE PARCELLE ». Les 59 543
          parcelles du registre sont à Faranah et à Tombola Plateau ; la carte
          s'ouvrait sur Kaloum, où il y en a deux. Sans cette mention, une
          carte juste se lit comme une couche cassée. */}
      {/* ⚠️ L'ALERTE SE FONDE SUR « AU PLAN », PAS SUR L'OPPOSABLE (D.779).
          Elle explique une carte VIDE : si rien n'est dessiné, c'est
          `parcellesAuPlan` qui vaut zéro. La fonder sur l'opposable
          l'afficherait AUJOURD'HUI, sur une carte qui porte 1,7 M d'emprises
          — un message qui contredit ce que l'agent voit. */}
      {couverture && couverture.parcellesAuPlan === 0 ? (
        <div className="carte-bandeau avert">
          Le registre ne porte aucune parcelle au plan : la carte est vide parce que
          le cadastre l'est, non parce qu'une couche manque.
        </div>
      ) : vueConseillee && (
        <div className="carte-bandeau carte-reperage">
          <strong>{vueConseillee.parcelles.toLocaleString('fr-FR')} parcelles</strong>
          {vueConseillee.division ? ` — ${vueConseillee.division}` : ''}
          {couverture && couverture.divisionsPeuplees > 1 &&
            ` · ${couverture.parcellesAuPlan.toLocaleString('fr-FR')} au total sur ${couverture.divisionsPeuplees} divisions`}
          {/* ⚠️⚠️ LE COMPTE OPPOSABLE EST DIT, ET C'EST TOUT L'OBJET DE D.779.
              Sans lui, « 1 761 157 parcelles » se lit comme un registre
              peuplé, alors que QUATRE emprises seulement portent un statut
              actif. La couverture n'est pas la propriété. */}
          {couverture && (
            <em className="compte-opposable">
              {couverture.parcellesOpposables === 0
                ? ' · aucune n’est encore au registre (statut actif)'
                : ` · dont ${couverture.parcellesOpposables.toLocaleString('fr-FR')} au registre (statut actif)`}
            </em>
          )}
          <button className="lien" onClick={() => {
            const c = carte.current;
            if (c) c.jumpTo({ center: vueConseillee.centre, zoom: vueConseillee.zoom });
          }}>Y aller</button>
        </div>
      )}

        {erreurTuiles && <div className="carte-bandeau erreur" role="alert">{erreurTuiles}</div>}
      </div>

      <div ref={conteneur} className="carte-toile" />

      <AttributionSources couches={couchesPresentes} />

      {/* ⚠️ La légende a quitté cette toile pour `legende-carte.tsx` — D.943 :
          posée en absolu elle recouvrait le plan ; dockée, elle vit dans la
          zone de grille `legende`, avec le basculeur du plan qu'elle décrit. */}
    </div>
  );
}
